import type {
  Comment,
  CommentThread,
  ReactionType,
} from '@/shared/types/comment';
import type { Person } from '../types';
import { Paths } from '../../constants';
import { apiInvoke } from '@/store/helpers/load';
import { makePayloadRequestInit } from '@/lib/api';
import type { APIParams } from '@/store/helpers/types';

export interface CommentsAccessResponse {
  allowed: boolean;
}

export interface LoadCommentsResponse {
  allowed: boolean;
  access: {
    comment_view: boolean;
    comment_create: boolean;
    comment_edit: boolean;
    comment_archive: boolean;
    comment_sensitive_view: boolean;
    comment_sensitive_flag: boolean;
  } | null;
  comments: Comment[];
  threads?: CommentThread[];
}

export class CommentApiError extends Error {
  code?: string;

  status: number;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = 'CommentApiError';
    this.status = status;
    this.code = code;
  }
}

async function commentApiRequest<T>(
  api: APIParams['api'],
  auth: APIParams['auth'],
  path: string,
  init: RequestInit,
): Promise<T> {
  const response = await api(path, auth.firebaseUser || null, init);
  const contentType = response.headers.get('Content-Type') || '';
  const body = contentType.startsWith('application/json')
    ? ((await response.json()) as { code?: string; message?: string })
    : { message: await response.text() };
  if (!response.ok) {
    throw new CommentApiError(
      response.status,
      body.message || 'Unable to complete the comment request.',
      body.code,
    );
  }

  return body as T;
}

export async function getCommentsAccess({
  api,
  auth,
  subjectId,
}: { subjectId: string } & APIParams) {
  const path = `${Paths.comments}/access?subject_id=${encodeURIComponent(subjectId)}`;
  const res = await apiInvoke<CommentsAccessResponse>(api, path, auth, {
    method: 'GET',
  });

  return res;
}

export async function loadComments({
  api,
  auth,
  subject,
}: { subject: Person } & APIParams) {
  const path = `${Paths.comments}/comments`;
  const subjectPcoId = subject.data.pco_id?.trim();
  const subjectMhxId = subject.data.mhx_id?.trim();
  const payload: LoadCommentsRequest = {
    subject_id: subject.id,
    ...(subjectPcoId ? { pco_id: subjectPcoId } : {}),
    ...(subjectMhxId ? { mhx_id: subjectMhxId } : {}),
  };
  const res = await apiInvoke<LoadCommentsResponse>(
    api,
    path,
    auth,
    makePayloadRequestInit(payload, 'POST'),
  );

  return res;
}

interface LoadCommentsRequest {
  subject_id: string;
  pco_id?: string;
  mhx_id?: string;
}

export async function postComment({
  api,
  auth,
  comment,
}: { comment: Comment } & APIParams) {
  const path = `${Paths.comments}/posts`;
  const payload: PostCommentRequest = comment.data;
  const res = await commentApiRequest<PostCommentResponse>(
    api,
    auth,
    path,
    makePayloadRequestInit(payload, 'POST'),
  );

  return res;
}

export async function updateComment({
  api,
  auth,
  comment,
}: {
  comment: { id: string; data: Partial<Comment['data']> };
} & APIParams) {
  const path = `${Paths.comments}/posts/${comment.id}`;
  const payload: UpdateCommentRequest = comment.data;
  const res = await commentApiRequest<UpdateCommentResponse>(
    api,
    auth,
    path,
    makePayloadRequestInit(payload, 'PATCH'),
  );

  return res;
}

export async function reactToComment({
  api,
  auth,
  id,
  userId,
  type,
  action,
}: {
  id: string;
  userId: string;
  type: ReactionType;
  action?: 'toggle' | 'add' | 'remove';
} & APIParams) {
  const path = `${Paths.comments}/posts/${id}/reactions`;
  const payload = { userId, type, action };
  const res = await apiInvoke<ReactToCommentResponse>(
    api,
    path,
    auth,
    makePayloadRequestInit(payload, 'POST'),
  );

  return res;
}

export async function createCommentThread({
  api,
  auth,
  subjectId,
  title,
  memberIds,
  expiresInDays,
}: {
  subjectId: string;
  title: string;
  memberIds: string[];
  expiresInDays: number;
} & APIParams) {
  return await commentApiRequest<CommentThread>(
    api,
    auth,
    `${Paths.comments}/threads`,
    makePayloadRequestInit(
      {
        subject_id: subjectId,
        title,
        member_ids: memberIds,
        expires_in_days: expiresInDays,
      },
      'POST',
    ),
  );
}

export async function updateCommentThread({
  api,
  auth,
  threadId,
  title,
  memberIds,
  expectedRevision,
}: {
  threadId: string;
  title: string;
  memberIds: string[];
  expectedRevision: number;
} & APIParams) {
  return await commentApiRequest<CommentThread>(
    api,
    auth,
    `${Paths.comments}/threads/${threadId}`,
    makePayloadRequestInit(
      {
        title,
        member_ids: memberIds,
        expected_revision: expectedRevision,
      },
      'PATCH',
    ),
  );
}

type ReactToCommentResponse = Comment;

type PostCommentRequest = Omit<
  Comment['data'],
  'created_at' | 'created_by' | 'updated_at'
>;

type PostCommentResponse = Comment;

type UpdateCommentRequest = Partial<PostCommentRequest>;

type UpdateCommentResponse = Comment;
